import React, { useState } from "react";
import styles from "./PurchaseList.module.scss";

import moment from "moment";
import { useNavigate } from "react-router-dom";
import { PurchaseProps } from "../../types/purchase";
import useConfirmPopup from "../../context/useConfirmPopup";
import { GET } from "../../services/axios.service";
import { toast } from "react-toastify";
import { toastStyles } from "../../static/styles";

type Props = {
  purchases: PurchaseProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeletePurchase: (id: number, password: string) => void;
};

const PurchaseList = ({
  purchases,
  pageNumber,
  pageSize,
  isLoading,
  handleDeletePurchase,
}: Props) => {
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditPurchase = (item: PurchaseProps) => {
    navigate(`/purchase/edit/${item.id}`);
  };

  const handleDeleteRow = async (id: number) => {
    const result = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (result && result.confirmed && result.password) {
      setIsActionLoading(id);
      await handleDeletePurchase(id, result.password);
      setIsActionLoading(0);
    }
  };

  const handleDownloadPdf = async (id: number) => {
    try {
      setIsActionLoading(id);
      const response: any = await GET(`/v1/admin/purchases/${id}/pdf`, {});
      if (response.data.status === "success") {
        const downloadLink = response.data.data.download_link;
        try {
          const newWindow = window.open(downloadLink, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
          }
        } catch (e) {
          window.location.href = downloadLink;
        }
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsActionLoading(0);
    }
  };

  const getItemNames = (item: PurchaseProps) => {
    if (item.details && item.details.length > 0) {
      return item.details
        .map((d) => d.item?.name || "-")
        .join(", ");
    }
    return "-";
  };

  return (
    <div className={styles.PurchaseList}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    બિલ નં.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    પાર્ટી
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    આઈટમ્સ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    રકમ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                  >
                    તારીખ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold tracking-wider"
                  >
                    એકશન્સ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      રાહ જુઓ...
                    </td>
                  </tr>
                ) : (
                  <>
                    {purchases.length > 0 ? (
                      <>
                        {purchases.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>બિલ નં.: {item.id}</div>
                              {item.voucher_no && (
                                <div>વાઉચર નં: {item.voucher_no}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.party_firm_name || "-"}
                              <br />
                              {(item.party_name ? item.party_name : "") +
                                (item.party_phone
                                  ? " (" + item.party_phone + ")"
                                  : "")}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-[200px]">
                              <span className="line-clamp-2">
                                {getItemNames(item)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900">
                              <>
                                સબ ટોટલ :{" "}
                                {Number(item.sub_total).toFixed(2)}
                                <br />
                                ડિસ્કાઉન્ટ :{" "}
                                {Number(item.discount).toFixed(2) || "0.00"}
                                <br />
                                ટેક્સ :{" "}
                                {Number(item.tax).toFixed(2) || "0.00"}
                                <br />
                                ટોટલ :{" "}
                                {Number(item.grand_total).toFixed(2) || "0.00"}
                              </>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {moment(item.date).format(
                                "DD-MM-YYYY hh:mm A"
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {isActionLoading === item.id ? (
                                <p className="text-primary-2 font-bold text-right text-lg">
                                  રાહ જુઓ...
                                </p>
                              ) : (
                                <>
                                  <button
                                    onClick={() =>
                                      handleDownloadPdf(item.id)
                                    }
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    PDF
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleEditPurchase(item)
                                    }
                                    className="text-indigo-600 hover:text-indigo-900 ml-4"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteRow(item.id)
                                    }
                                    className="text-red-600 hover:text-red-900 ml-4"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          ખરીદી ની વિગતો મળી નથી
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PurchaseList;
